import Vue from 'vue';
import Cookies from 'js-cookie';
import {RootState} from './typings';
import {Client, Country, Event, Order, Product, Slot, State, Title, User, VisitorSegment, PaymentMethod} from '../Models';
import {
  CLEAR_BUYER_DATA,
  CLEAR_IS_GUEST_ORDER,
  CLEAR_LOGIN_RETURN_URL,
  CLEAR_REGISTRATION_CODES,
  CLEAR_SESSION,
  DELETE_CART,
  INIT_BUYER_DATA,
  INIT_CART,
  INIT_IS_GUEST_ORDER,
  INIT_LOGIN_RETURN_URL,
  INIT_REGISTRATION_CODES,
  INIT_SESSION,
  SET_BUYER_DATA,
  SET_COUNTRIES,
  SET_ENABLE_TICKET_SHOP,
  SET_IS_GUEST_ORDER,
  SET_LANGUAGE,
  SET_LOGIN_RETURN_URL,
  SET_REGISTRATION_CODES,
  SET_SESSION,
  SET_STATES,
  SET_TITLES,
  UPDATE_CART,
  UPDATE_CLIENTS,
  UPDATE_EVENTS,
  UPDATE_ORDER,
  UPDATE_ORDER_ITEM,
  UPDATE_PRODUCTS,
  UPDATE_EXHIBITOR_DATA,
  UPDATE_SLOTS,
  INIT_USER,
  SET_USER,
  CLEAR_USER, UPDATE_VISITOR_SEGMENTS, UPDATE_PAYMENT_METHODS, UPDATE_ORDER_ITEM_CONFIGURATION,
} from './commits';
import {LocalStorageUtility} from '../Utilities';
import {CartDTO, CustomerDataDTO, RegistrationCodesDTO, SessionDTO} from '../DTOs';

export const CART_STORAGE_KEY = 'currentCart';
export const USER_STORAGE_KEY = 'lmsUser';
export const BUYER_DATA_STORAGE_KEY = 'buyerData';
export const IS_GUEST_ORDER_STORAGE_KEY = 'isGuestOrder';
export const SESSION_STORAGE_KEY = 'authSession';
export const REGISTRATION_CODES_STORAGE_KEY = 'registrationCodes';
export const LOGIN_RETURN_URL_STORAGE_KEY = 'loginReturnUrl';
export const SESSION_COOKIE_NAME = 'FAIR_LOGIN_SESSIONID';

export const mutations = {
  [INIT_CART](state: RootState) {
    state.cart = LocalStorageUtility.load<CartDTO>(CART_STORAGE_KEY, new CartDTO());
  },
  [SET_ENABLE_TICKET_SHOP](state: RootState, enable: boolean) {
    state.enableTicketShop = enable;
  },
  [UPDATE_CART](state: RootState, cart: CartDTO) {
    LocalStorageUtility.set(CART_STORAGE_KEY, cart);
    state.cart = LocalStorageUtility.load<CartDTO>(CART_STORAGE_KEY, new CartDTO());
  },
  [UPDATE_ORDER](state: RootState, order: Order) {
    if (state.cart) {
      Vue.set(state.cart, 'order', order);
      LocalStorageUtility.set(CART_STORAGE_KEY, state.cart);
      state.cart = LocalStorageUtility.load<CartDTO>(CART_STORAGE_KEY, new CartDTO());
    }
  },
  [UPDATE_ORDER_ITEM](state: RootState, {orderItem, data}) {
    if (state.cart && state.cart.order.orderItems) {
      const items = state.cart.order.orderItems;
      for (const localOrderItem of items) {
        if (localOrderItem.id === orderItem.id) {
          Object.keys(data).forEach((k: string) => {
            Vue.set(localOrderItem, k, data[k]);
          });
        }
      }
      LocalStorageUtility.set(CART_STORAGE_KEY, state.cart);
      state.cart = LocalStorageUtility.load<CartDTO>(CART_STORAGE_KEY, new CartDTO());
    }
  },
  [UPDATE_ORDER_ITEM_CONFIGURATION](state: RootState, {orderItemConfig, data}) {
    if (state.cart && state.cart.order.additionalOrderItemConfigurations) {
      const configs = state.cart.order.additionalOrderItemConfigurations;
      for (const localConfig of configs) {
        if (localConfig.id === orderItemConfig.id) {
          Object.keys(data).forEach((k: string) => {
            Vue.set(localConfig, k, data[k]);
          });
        }
      }
    }
  },
  [DELETE_CART](state: RootState) {
    LocalStorageUtility.delete(CART_STORAGE_KEY);
    state.cart = null;
  },
  [UPDATE_PRODUCTS](state: RootState, products: Array<Product>) {
    state.products = products;
  },
  [UPDATE_EVENTS](state: RootState, events: Array<Event>) {
    state.events = events;
  },
  [UPDATE_CLIENTS](state: RootState, clients: Array<Client>) {
    state.clients = clients;
  },
  [UPDATE_SLOTS](state: RootState, slots: Array<Slot>) {
    state.slots = slots;
  },
  [UPDATE_PAYMENT_METHODS](state: RootState, paymentMethods: Array<PaymentMethod>) {
    state.paymentMethods = paymentMethods;
  },
  [UPDATE_EXHIBITOR_DATA](state: RootState, exhibitorData: {names: Array<string>, advertising: any}) {
    state.exhibitorData = exhibitorData;
  },
  [UPDATE_VISITOR_SEGMENTS](state: RootState, visitorSegments: Array<VisitorSegment>) {
    state.visitorSegments = visitorSegments;
  },
  [SET_LANGUAGE](state: RootState, language: string) {
    state.language = language;
  },
  [INIT_USER](state: RootState) {
    state.user = LocalStorageUtility.load<User>(USER_STORAGE_KEY, new User({}));
  },
  [SET_USER](state: RootState, user: User) {
    LocalStorageUtility.set(USER_STORAGE_KEY, user);
    state.user = LocalStorageUtility.load<User>(USER_STORAGE_KEY, new User({}));
  },
  [CLEAR_USER](state: RootState) {
    state.user = null;
    LocalStorageUtility.delete(USER_STORAGE_KEY);
  },
  [INIT_BUYER_DATA](state: RootState) {
    state.buyerData = LocalStorageUtility.load<CustomerDataDTO>(BUYER_DATA_STORAGE_KEY, new CustomerDataDTO());
  },
  [SET_BUYER_DATA](state: RootState, buyerData: CustomerDataDTO) {
    LocalStorageUtility.set(BUYER_DATA_STORAGE_KEY, buyerData);
    state.buyerData = LocalStorageUtility.load<CustomerDataDTO>(BUYER_DATA_STORAGE_KEY, new CustomerDataDTO());
  },
  [CLEAR_BUYER_DATA](state: RootState) {
    state.buyerData = null;
    LocalStorageUtility.delete(BUYER_DATA_STORAGE_KEY);
  },
  [INIT_IS_GUEST_ORDER](state: RootState) {
    state.isGuestOrder = LocalStorageUtility.loadPrimitive(IS_GUEST_ORDER_STORAGE_KEY) === true;
  },
  [SET_IS_GUEST_ORDER](state: RootState, value: boolean) {
    state.isGuestOrder = value;
    LocalStorageUtility.setPrimitive(IS_GUEST_ORDER_STORAGE_KEY, value);
  },
  [CLEAR_IS_GUEST_ORDER](state: RootState) {
    state.isGuestOrder = null;
    LocalStorageUtility.delete(IS_GUEST_ORDER_STORAGE_KEY);
  },
  [INIT_SESSION](state: RootState) {
    state.session = LocalStorageUtility.load<SessionDTO>(SESSION_STORAGE_KEY, new SessionDTO());
    if (state.session && state.session.session) {
      Cookies.set(SESSION_COOKIE_NAME, state.session.session, {secure: true, sameSite: 'strict'});
    } else {
      Cookies.remove(SESSION_COOKIE_NAME);
    }
  },
  [SET_SESSION](state: RootState, session: SessionDTO) {
    LocalStorageUtility.set<SessionDTO>(SESSION_STORAGE_KEY, session);
    state.session = LocalStorageUtility.load<SessionDTO>(SESSION_STORAGE_KEY, new SessionDTO());
    if (state.session && state.session.session) {
      Cookies.set(SESSION_COOKIE_NAME, session.session, {secure: true, sameSite: 'strict'});
    } else {
      Cookies.remove(SESSION_COOKIE_NAME);
    }
  },
  [CLEAR_SESSION](state: RootState) {
    LocalStorageUtility.delete(SESSION_STORAGE_KEY);
    LocalStorageUtility.delete(BUYER_DATA_STORAGE_KEY);
    LocalStorageUtility.delete(LOGIN_RETURN_URL_STORAGE_KEY);
    Cookies.remove(SESSION_COOKIE_NAME);
    state.session = null;
    state.buyerData = null;
  },
  [SET_REGISTRATION_CODES](state: RootState, registrationCodes: Array<string>) {
    const dto = new RegistrationCodesDTO();
    dto.registrationCodes = registrationCodes;
    LocalStorageUtility.set<RegistrationCodesDTO>(REGISTRATION_CODES_STORAGE_KEY, dto);
    const storageDto = LocalStorageUtility.load<RegistrationCodesDTO>(REGISTRATION_CODES_STORAGE_KEY, new RegistrationCodesDTO());
    if (storageDto) {
      state.registrationCodes = storageDto?.registrationCodes;
    }
  },
  [CLEAR_REGISTRATION_CODES](state: RootState) {
    LocalStorageUtility.delete(REGISTRATION_CODES_STORAGE_KEY);
    state.registrationCodes = null;
  },
  [INIT_REGISTRATION_CODES](state: RootState) {
    const storageDto = LocalStorageUtility.load<RegistrationCodesDTO>(REGISTRATION_CODES_STORAGE_KEY, new RegistrationCodesDTO());
    if (storageDto) {
      state.registrationCodes = storageDto?.registrationCodes;
    }
  },
  [SET_TITLES](state: RootState, titles: Array<Title>) {
    state.titles = titles;
  },
  [SET_COUNTRIES](state: RootState, countries: Array<Country>) {
    state.countries = countries;
  },
  [SET_STATES](state: RootState, states: { [key: string]: Array<State> }) {
    state.states = states;
  },
  [SET_LOGIN_RETURN_URL](state: RootState, returnUrl: string | null) {
    state.loginReturnUrl = returnUrl ? returnUrl.trim() : returnUrl;
    if (returnUrl) {
      LocalStorageUtility.setPrimitive(LOGIN_RETURN_URL_STORAGE_KEY, returnUrl);
    } else {
      LocalStorageUtility.delete(LOGIN_RETURN_URL_STORAGE_KEY);
    }
  },
  [CLEAR_LOGIN_RETURN_URL](state: RootState) {
    state.loginReturnUrl = null;
    LocalStorageUtility.delete(LOGIN_RETURN_URL_STORAGE_KEY);
  },
  [INIT_LOGIN_RETURN_URL](state: RootState) {
    const returnUrl = `${LocalStorageUtility.loadPrimitive(LOGIN_RETURN_URL_STORAGE_KEY) || ''}`;
    if (returnUrl && returnUrl.trim()) {
      state.loginReturnUrl = returnUrl;
    }
  },
};
